/**
 * The journal's read, and the one thing it writes — ROADMAP 9.5.
 *
 * Validated at the boundary for the same reason every other read in this app
 * is: `api.get<T>` is a type ASSERTION, and a shape that changed under us
 * reaches `.map` and takes the page down. A malformed trade is dropped rather
 * than failing the whole read — the others are real records and the trader
 * came here to read them.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, qs } from '@/lib/api'
import type { JournalState, JournalTrade } from '../types'

/*
 * Boundary validation, and it lives with the hook rather than in `types/`
 * because it is not a type — it is the one place that decides whether what
 * came off the wire may be trusted. `api.get<T>` is an ASSERTION, so without
 * this a shape that changed under us reaches `.map` and takes the page down.
 */
const isTrade = (v: unknown): v is JournalTrade => {
  if (typeof v !== 'object' || v === null) return false
  const t = v as JournalTrade
  return (
    typeof t.tradeId === 'string' &&
    typeof t.gross === 'number' &&
    typeof t.net === 'number' &&
    Array.isArray(t.legs) &&
    Array.isArray(t.events) &&
    Array.isArray(t.journalTags)
  )
}


export function useJournal(underlying: string | null): {
  state: JournalState
  /** Replace one trade in place, after a review is saved. */
  patch: (tradeId: string, next: Partial<JournalTrade>) => void
  reload: () => void
} {
  const [state, setState] = useState<JournalState>({ kind: 'LOADING' })
  const [nonce, setNonce] = useState(0)
  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    setState({ kind: 'LOADING' })
    void api
      .get<unknown>(`/trades/journal${qs({ underlying: underlying ?? undefined })}`)
      .then((r) => {
        if (cancelled) return
        const rows = (r as { trades?: unknown })?.trades
        if (!Array.isArray(rows)) {
          setState({
            kind: 'FAILED',
            why: 'the journal came back in a shape this build does not understand',
          })
          return
        }
        setState({ kind: 'READY', trades: rows.filter(isTrade) })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setState({
          kind: 'FAILED',
          why: e instanceof Error ? e.message : 'the journal could not be read',
        })
      })
    return () => {
      cancelled = true
    }
  }, [underlying, nonce])

  /*
   * Patched in place rather than re-fetched. A saved lesson changes one row
   * and nothing else; re-reading the whole journal would collapse the open
   * row and scroll the page away from what the trader was writing about.
   */
  const patch = useCallback((tradeId: string, next: Partial<JournalTrade>) => {
    setState((s) =>
      s.kind !== 'READY'
        ? s
        : {
            kind: 'READY',
            trades: s.trades.map((t) => (t.tradeId === tradeId ? { ...t, ...next } : t)),
          },
    )
  }, [])

  return { state, patch, reload }
}
